import { createRouter, createWebHistory } from 'vue-router'

import DepositHome from '@/views/DepositHome.vue'
import DepositFileHome from '@/views/DepositFileHome.vue'
import AdminHome from '@/views/AdminHome.vue'
import WelcomePage from '@/views/WelcomePage.vue'
import HelpPage from '@/views/HelpPage.vue'
import BeforeYouStart from '@/views/BeforeYouStart.vue'
import DataProtection from '@/views/DataProtection.vue'
import ProfilePage from '@/components/ProfilePage.vue'
import SignIn from '@/components/SignIn.vue'
import AnswerChallenge from '@/components/AnswerChallenge.vue'
import UserAdmin from '@/views/UserAdmin.vue'
import { hardSignOut } from '@/utils/nuclear-signout'
import { fetchAuthSession } from 'aws-amplify/auth'
import AuthCallback from '@/views/AuthCallback.vue'

async function requireAuth(to) {
    try {
        const { tokens } = await fetchAuthSession({ forceRefresh: true })
        console.log('tokens:', tokens)
        console.log('idToken:', tokens?.idToken)
        if (!tokens?.idToken) return { name: 'SignIn', query: { redirect: to.fullPath } }
        return true
    } catch (e) {
        console.error('requireAuth threw:', e)
        return { name: 'SignIn', query: { redirect: to.fullPath } }
    }
}

async function signOut() {
    await hardSignOut({ reload: true })   // no Hosted UI redirect
    return { name: 'SignIn' }             // fallback (won’t usually run due to reload)
}

const routes = [
    {
        path: '/',
        name: 'WelcomePage',
        component: WelcomePage,
        alias: '/home',
        meta: {
            breadCrumb: [
                {
                    text: 'Deposit Service'
                }
            ]
        }
    },
    {
        path: '/help',
        name: 'HelpPage',
        component: HelpPage,
        meta: {
            breadCrumb: [
                {
                    to: { name: 'WelcomePage' },
                    text: 'Deposit Service'
                },
                {
                    text: 'Help'
                }
            ]
        }
    },
    {
        path: '/beforeyoustart',
        name: 'BeforeYouStart',
        component: BeforeYouStart,
        meta: {
            breadCrumb: [
                {
                    to: { name: 'WelcomePage' },
                    text: 'Deposit Service'
                },
                {
                    text: 'Before You Start'
                }
            ]
        }
    },
    {
        path: '/dataprotection',
        name: 'DataProtection',
        component: DataProtection,
        meta: {
            breadCrumb: [
                {
                    to: { name: 'WelcomePage' },
                    text: 'Deposit Service'
                },
                {
                    text: 'Data Protection'
                }
            ]
        }
    },
    {
        path: '/admin',
        name: 'AdminHome',
        component: AdminHome,
        beforeEnter: requireAuth,
        meta: {
            breadCrumb: [
                {
                    to: { name: 'WelcomePage' },
                    text: 'Deposit Service'
                },
                {
                    text: 'Admin Homepage'
                }
            ]
        }
    },
    {
        path: '/me',
        name: 'ProfilePage',
        component: ProfilePage,
        beforeEnter: requireAuth,
        meta: {
            breadCrumb: [
                {
                    to: { name: 'WelcomePage' },
                    text: 'Deposit Service'
                },
                {
                    text: 'My Profile'
                }
            ]
        }
    },
    {
        path: '/signin',
        name: 'SignIn',
        component: SignIn,
        meta: {
            breadCrumb: [
                {
                    to: { name: 'WelcomePage' },
                    text: 'Deposit Service'
                },
                {
                    text: 'Sign-in'
                }
            ]
        }
    },
    {
        path: '/answer-challenge',
        name: 'AnswerChallenge',
        component: AnswerChallenge
    },
    {
        path: '/auth',
        name: 'authCallback',
        component: AuthCallback,
    },
    {
        path: '/signout',
        name: 'signOut',
        beforeEnter: signOut
    },
    {
        path: '/users',
        name: 'Users',
        component: UserAdmin,
        meta: {
            breadCrumb: [
                {
                    to: { name: 'WelcomePage' },
                    text: 'Deposit Service'
                },
                {
                    text: 'Users'
                }
            ]
        }
    },
    {
        path: '/deposits',
        name: 'DepositHome',
        component: DepositHome,
        beforeEnter: requireAuth,
        meta: {
            breadCrumb: [
                {
                    to: { name: 'WelcomePage' },
                    text: 'Deposit Service'
                },
                {
                    text: 'Your deposits'
                }
            ]
        }
    },
    {
        path: '/deposits/:deposit_id',
        name: 'DepositFileHome',
        component: DepositFileHome,
        props: (route) => ({
            depositor: route.query.depositor || '',
            deposit_id: route.params.deposit_id,
            deposit_name: route.query.deposit_name || '',
            created_date: route.query.created_date || '',
        }),
        meta: {
            breadCrumb(route) {
                const deposit_name = route.query.deposit_name || route.params.deposit_id;
                const adminUser = route.query.admin_user;
                const depositsCrumb = adminUser
                    ? {
                        to: {
                            path: '/admin',
                            query: {
                                restore_user: adminUser,
                                ...(route.query.admin_user_name ? { admin_user_name: route.query.admin_user_name } : {}),
                                ...(route.query.admin_search ? { admin_search: route.query.admin_search } : {}),
                                ...(route.query.admin_page ? { admin_page: route.query.admin_page } : {}),
                            }
                        },
                        text: route.query.admin_user_name || adminUser,
                    }
                    : {
                        to: { name: 'DepositHome' },
                        text: 'Your deposits',
                    };
                return [
                    {
                        to: { name: 'WelcomePage' },
                        text: 'Deposit Service'
                    },
                    depositsCrumb,
                    {
                        text: deposit_name,
                    }
                ]
            }
        }
    }
]

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
})

export default router
